/* eslint-disable react/display-name */
/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { Button as VButton } from '@veneer/core';
import React, { forwardRef, memo, useMemo } from 'react';
import './styles.scss';
import { ButtonProps } from './types';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit button component based on Veneer button
 * @public
 */
const TButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    id = generateID('clientos-ui-toolkit-button'),
    children,
    type,
    size,
    disabled,
    icon,
    key,
    expanded,
    onClick,
    ...rest
  } = props;
  const cls = useMemo(() => {
    let cls = 'clientos-ui-toolkit-button label';
    cls += ` clientos-ui-toolkit-button__${type ?? 'primary'}`;
    cls += ` clientos-ui-toolkit-button__${size ?? 'large'}`;
    if (disabled) cls += ' clientos-ui-toolkit-button__disabled';
    const isIconButton = Boolean(icon && !children);
    if (isIconButton) cls += ' clientos-ui-toolkit-button__icon-button';
    if (expanded) cls += ' expanded';
    return cls;
  }, [children, type, size, disabled, icon, expanded]);
  return (
    <VButton
      id={id}
      ref={ref}
      key={key}
      className={cls}
      disabled={disabled}
      leadingIcon={icon}
      onClick={onClick}
      expanded={expanded}
      // Fix for tabbing issue in Mac Safari
      tabIndex={0}
      {...rest}
    >
      {React.Children.count(children) > 0 && (
        <div className={`${size === 'small' ? 'caption-large' : ''}`}>
          {children && <span className="label">{children}</span>}
        </div>
      )}
    </VButton>
  );
});

export const ButtonAnalytics = withAnalytics(TButton);
export default memo(TButton);
